import { drawSkierSprite } from './skierSprite';
import { RIDER_COLORS } from '../../shared/rider';
import { spriteScaleFor } from '../../utils/canvasSizing';
import type { Obstacle } from '../../types/game.types';
import { BackgroundScroller } from './Background';
import { Explosion } from './Explosion';
import { getSlopeBounds } from './SlopeConfig';
import { SeededLanePath } from './SeededPath';
import { SnowPlowParticles } from './SnowPlowParticles';
import { maskPlayerName, shownRiderChroma } from '../../utils/displayName';
import { formatMoney } from '../../utils/money';

import SkiierHardLeft  from '../../assets/SkiierHardLeft.png';
import SkiierSoftLeft  from '../../assets/SkiierSoftLeft.png';
import SkiierSoftRight from '../../assets/SkiierSoftRight.png';
import SkiierHardRight from '../../assets/SkiierHardRight.png';
import SkiierDown      from '../../assets/SkiierDown.png';
import SkiierAbruptSwerve     from '../../assets/SkiierAbruptSwerve.png';
import SkiierAbruptSwerveSoft from '../../assets/SkiierAbruptSwerveSoft.png';
import SkiierIdleRestV3       from '../../assets/SkiierIdleRestV3.png';

const _imgCache = new Map<string, HTMLImageElement>();
function cachedImg(src: string): HTMLImageElement {
  if (!_imgCache.has(src)) {
    const img = new Image();
    img.decoding = 'async';
    img.src = src;
    _imgCache.set(src, img);
    img.decode?.().catch(() => undefined);
  }
  return _imgCache.get(src)!;
}

[
  SkiierHardLeft,
  SkiierSoftLeft,
  SkiierSoftRight,
  SkiierHardRight,
  SkiierDown,
  SkiierAbruptSwerve,
  SkiierAbruptSwerveSoft,
  SkiierIdleRestV3,
].forEach(cachedImg);

export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function deriveSeed(serverSeedHash: string, playerId: string): number {
  const hashPart = parseInt((serverSeedHash || '00000000').slice(0, 8), 16) || 0x12345678;
  let idPart = 0;
  for (let i = 0; i < playerId.length; i++) idPart = ((idPart * 31) + playerId.charCodeAt(i)) | 0;
  return (hashPart ^ idPart) >>> 0;
}

export const CHROMA_PALETTES = RIDER_COLORS.map(color => ({
  glow: color.hex, label: color.hex, tagBg: `${color.hex}20`,
}));

export type SkierState = 'QUEUED' | 'ROLLING_TO_START' | 'STAGED' | 'ENTERING' | 'RACING' | 'CASHING_OUT' | 'EXITED' | 'CRASHED';
export type CrashPresentation = 'explosion' | 'avalanche' | 'snowball' | 'snow_slab' | 'cliff';

export interface SkierPlayerInfo {
  playerId:   string;
  username:   string;
  betAmount:  number;
  chroma:     number;
  prnSeed:    number;
  reconnect?: boolean;
  /** This browser's own rider: tagged "YOU" (drawn locally; nothing extra is sent to anyone). */
  isSelf?:    boolean;
}

export interface PresentationSlot {
  index:         number;
  total:         number;
  anchorNormX:   number;
  targetY:       number;
  swayAmp:       number;
  swayFreq:      number;
  swayPhase:     number;
  spacingRadius: number;
}

// ─── Physics constants ────────────────────────────────────────────────────────

const SW = 50, SH = 60, RACE_Y = 105, MAX_TRACK = 24;
const START_LINE_CENTER_Y = 36;
const START_LINE_Y_SYNC = 0.34;
const ENTRY_FRAMES_TOP = 58;
const ENTRY_FRAMES_PROMOTED = 44;
const ENTRY_FRAMES_PROMOTED_TOP = 102;
const CASHOUT_HARD_SWERVE_FRAMES = 38;
const CASHOUT_SOFT_SWERVE_FRAMES = 12;
const FORMATION_Y_PULL = 0.024;
const FORMATION_X_BLEND = 0.12;
const STEER_TARGET_SMOOTH = 0.075;
const STEER_ACCEL_LIMIT = 0.34;

/**
 * How wide the skier's collision box is for avoidance purposes.
 * Wider than SW so the skier gives obstacles a visible berth.
 */
const SKIER_AVOID_HALF = 34;

/**
 * Padding added to each side of an obstacle's bounding box.
 * Trees can be ~80 px wide at max scale; rocks ~72 px.
 * Using a generous pad ensures the skier steers clear before overlap.
 */
const OBS_PAD = 28;

/**
 * Minimum viable gap width the skier will attempt to thread.
 * Must be at least SKIER_AVOID_HALF*2 + a comfort margin.
 */
const MIN_GAP = SKIER_AVOID_HALF * 2 + 20; // 88 px

/**
 * The skier picks a steering target and commits to it for this many frames
 * before re-evaluating.  This is the primary jitter-killer: without commitment
 * the skier recalculates every frame and oscillates between near-equal gaps.
 */
const COMMIT_FRAMES = 18;

/**
 * Lookahead distances (px) for two scanning tiers:
 *  - NEAR: obstacles in this band are treated as immediate — maximum urgency.
 *  - FAR:  used for early warning and gentle pre-steering.
 *
 * The background scroller tops out at ~25 px/frame so FAR=420 gives ~16 frames
 * of warning at max speed.
 */
const NEAR_AHEAD = 160;
const FAR_AHEAD  = 420;
const BEHIND_SCAN = 30; // still track obstacles slightly behind

/**
 * How quickly homeX drifts toward the skier's actual X each frame.
 * 0.004 per frame ≈ 22% of the remaining gap per second at 60fps — slow
 * enough to read as a gradual lane change rather than a snap-back.
 */
const HOME_DRIFT_RATE = 0.004;

// ─── Path-following constants ─────────────────────────────────────────────────

/**
 * The skier looks this many seconds ahead on its SeededLanePath to choose a
 * steering target.  Pre-reading the path means velocity is already building
 * before the waypoint arrives, producing smooth arcs rather than reactive
 * corrections.
 */
const PATH_LOOKAHEAD_SEC = 0.55;

/** Soft cap on path-following lateral speed. */
const PATH_MAX_VX = 4.8;

/**
 * Steering gain toward the pre-read path target.
 * Lower = smoother arcs but slower response.
 */
const PATH_STEER = 0.065;


// ─── SkierEntity ─────────────────────────────────────────────────────────────

export class SkierEntity {
  public readonly playerId:  string;
  public readonly username:  string;
  public readonly isSelf:    boolean;
  public readonly betAmount: number;
  private readonly baseChroma: number;
  /** The colour drawn: the rider's own, or their default once this player hid them. */
  get chroma(): number { return shownRiderChroma(this.baseChroma, this.playerId) ?? this.baseChroma; }

  public state:             SkierState  = 'QUEUED';
  public cashoutMultiplier: number|null = null;

  private x = 0;
  private y = 0;
  private vx = 0;
  private tilt = 0;
  private targetTilt = 0;
  private crashPresentation: CrashPresentation = 'explosion';
  private crashFrames = 0;
  private crashFallFrames = 0;
  private crashSpin = 0;
  private crashDirection = 1;

  private lanePath:   SeededLanePath | null = null;
  private elapsedSec: number               = 0;
  private slot: PresentationSlot | null = null;
  private presentationSeed = 0;

  private rand:  () => number;
  private homeX: number = 0.5;  // normalised [0,1] home lane centre

  private enterTargetX  = 0;
  private enterTargetY  = RACE_Y;
  private enterStartX   = 0;
  private enterStartY   = -SH - 20;
  private enterCtrlX    = 0;
  private enterCtrlY    = 0;
  private entryProgress = 0;
  private entryDuration = ENTRY_FRAMES_TOP;
  private entryMinProgress = 0.012;
  private entryDelayFrames = 0;
  private cashoutTimer  = 0;
  private exitDrift     = 0;
  private cashoutStartX = 0;
  private cashoutStartY = 0;
  private cashoutCarveDir = 1;
  private cashoutTargetX = 0;
  private moneyPopAlpha = 1.0;

  private skiTracks: { x: number; y: number }[] = [];
  private snowPlow: SnowPlowParticles;
  private canvasWidth:  number;
  private spriteScale: number;
  private canvasHeight: number;

  /**
   * The sprite key that is currently displayed.  Stored so _chooseSprite can
   * apply hysteresis — a sprite is only swapped when tilt moves far enough
   * *past* the entry threshold of the new sprite, preventing chattering when
   * tilt hovers near a boundary.
   */
  private currentSpriteKey: 'hardLeft' | 'softLeft' | 'down' | 'softRight' | 'hardRight' = 'down';

  public readonly explosion: Explosion;

  // ── Steering commitment state ──────────────────────────────────────────────
  /**
   * The X position (left edge of sprite) the skier is currently steering toward.
   * Null means no committed target yet (re-evaluate next frame).
   */
  private steerTargetX: number | null = null;
  private smoothedTargetX: number | null = null;
  /** Frames remaining before the skier may re-evaluate its steering target. */
  private commitFrames = 0;

  constructor(info: SkierPlayerInfo, canvasWidth: number, canvasHeight: number) {
    this.playerId     = info.playerId;
    this.username     = info.username;
    this.isSelf       = info.isSelf === true;
    this.betAmount    = info.betAmount;
    this.baseChroma   = Math.max(0, Math.min(CHROMA_PALETTES.length - 1, info.chroma));
    this.canvasWidth  = canvasWidth;
    this.canvasHeight = canvasHeight;
    this.spriteScale  = spriteScaleFor(canvasWidth);
    this.rand         = mulberry32(info.prnSeed);
    this.presentationSeed = info.prnSeed >>> 0;
    this.homeX        = 0.15 + this.rand() * 0.70; // keep away from hard edges
    this.explosion    = new Explosion();
    this.snowPlow     = new SnowPlowParticles();
    this.x            = canvasWidth / 2 - SW / 2;
    this.y            = -SH - 20;
  }

  setPath(path: SeededLanePath) { this.lanePath = path; }

  setPresentationSlot(slot: PresentationSlot) {
    this.slot = slot;
    if (this.state !== 'ROLLING_TO_START' && this.state !== 'STAGED') {
      this.enterTargetY = slot.targetY;
    }
    const { left, right } = getSlopeBounds(this.canvasWidth);
    const anchorMid = left + slot.anchorNormX * (right - left);
    this.homeX = Math.max(0.04, Math.min(0.96, (anchorMid - left) / (right - left)));
  }

  // ── State transitions ──────────────────────────────────────────────────────

  enterFromTop(slotX: number) {
    this.state        = 'ENTERING';
    this.enterTargetX = slotX - SW / 2;
    this.enterTargetY = this.slot?.targetY ?? RACE_Y;
    this.enterStartX  = this.enterTargetX + (this.rand() - 0.5) * 110;
    this.enterStartY  = -SH - 34 - this.rand() * 46;
    this.enterCtrlX   = this.enterTargetX + (this.rand() - 0.5) * 170;
    this.enterCtrlY   = this.enterTargetY - 92 - this.rand() * 34;
    this.entryProgress = 0;
    this.entryDuration = ENTRY_FRAMES_TOP + Math.round(this.rand() * 14);
    this.entryMinProgress = 0.012;
    this.entryDelayFrames = 0;
    this.x            = this.enterStartX;
    this.y            = this.enterStartY;
    this._resetMotionState();
    this.smoothedTargetX = this.enterTargetX;
  }

  rollToStartLine(slotX: number) {
    const targetY = this._startLineY();
    const continueFromCurrent = this.state !== 'QUEUED' && this.y > -SH;
    this.state        = 'ROLLING_TO_START';
    this.enterTargetX = slotX - SW / 2;
    this.enterTargetY = targetY;
    this.enterStartX  = continueFromCurrent ? this.x : this.enterTargetX + (this.rand() - 0.5) * 92;
    this.enterStartY  = continueFromCurrent ? this.y : -SH - 18 - this.rand() * 34;
    this.enterCtrlX   = this.enterTargetX + (this.rand() - 0.5) * 126;
    this.enterCtrlY   = Math.min(this.enterStartY, targetY) - 44 - this.rand() * 22;
    this.entryProgress = 0;
    this.entryDuration = 62 + Math.round(this.rand() * 22);
    this.entryMinProgress = 0.012;
    this.entryDelayFrames = continueFromCurrent ? 0 : Math.round((this.slot?.index ?? 0) * 5 + this.rand() * 8);
    this.x            = this.enterStartX;
    this.y            = this.enterStartY;
    this._resetMotionState();
    this.smoothedTargetX = this.enterTargetX;
  }

  retargetStartLine(slotX: number) {
    if (this.state === 'STAGED') {
      this.enterTargetX = slotX - SW / 2;
      this.enterTargetY = this._startLineY();
      this.smoothedTargetX = this.enterTargetX;
      return;
    }
    if (this.state === 'ROLLING_TO_START') {
      this.enterTargetX = slotX - SW / 2;
      this.enterTargetY = this._startLineY();
      this.smoothedTargetX = this.enterTargetX;
      return;
    }
    this.rollToStartLine(slotX);
  }

  stageAtStartLine(slotX: number) {
    this.state        = 'STAGED';
    this.enterTargetX = slotX - SW / 2;
    this.enterTargetY = this._startLineY();
    this.x            = this.enterTargetX + (this.rand() - 0.5) * 10;
    this.y            = this.enterTargetY;
    this._resetMotionState();
    this.entryDelayFrames = 0;
    this.smoothedTargetX = this.enterTargetX;
  }

  beginLaunch() {
    if (this.state !== 'STAGED' && this.state !== 'ROLLING_TO_START') return;
    const fromLeft = (this.slot?.index ?? 0) % 2 === 0;
    this.enterStartX = this.x;
    this.enterStartY = this.y;
    this.enterTargetY = this.slot?.targetY ?? RACE_Y;
    this.enterCtrlX = this.enterTargetX + (fromLeft ? -62 : 62) + (this.rand() - 0.5) * 32;
    this.enterCtrlY = this.enterTargetY - 54 - this.rand() * 30;
    this.entryProgress = 0;
    this.entryDuration = 72 + Math.round(this.rand() * 28);
    this.entryMinProgress = 0.012;
    this.entryDelayFrames = Math.round((this.slot?.index ?? 0) * 4 + this.rand() * 12);
    this._resetMotionState(false);
    this.state = 'ENTERING';
  }

  enterFromSide(fromLeft: boolean) {
    this.state        = 'ENTERING';
    const { left, right } = getSlopeBounds(this.canvasWidth);
    const slotX = this.slot
      ? left + this.slot.anchorNormX * (right - left) - SW / 2
      : left + this.rand() * (right - left - SW);
    this.enterTargetX = slotX;
    this.enterTargetY = this.slot?.targetY ?? RACE_Y;
    this.enterStartX  = Math.max(left - 20, Math.min(right - SW + 20, this.enterTargetX + (fromLeft ? -150 : 150) + (this.rand() - 0.5) * 50));
    this.enterStartY  = this.enterTargetY - 76 - this.rand() * 26;
    this.enterCtrlX   = this.enterTargetX + (fromLeft ? -76 : 76) + (this.rand() - 0.5) * 40;
    this.enterCtrlY   = this.enterTargetY - 38 - this.rand() * 36;
    this.entryProgress = 0;
    this.entryDuration = ENTRY_FRAMES_PROMOTED + Math.round(this.rand() * 10);
    this.entryMinProgress = 0.012;
    this.entryDelayFrames = 0;
    this.x            = this.enterStartX;
    this.y            = this.enterStartY;
    this._resetMotionState();
    this.vx           = fromLeft ? 2.4 : -2.4;
    this.smoothedTargetX = this.enterTargetX;
  }

  enterFromPromotedTop(slotX: number) {
    this.state        = 'ENTERING';
    const { left, right } = getSlopeBounds(this.canvasWidth);
    this.enterTargetX = slotX - SW / 2;
    this.enterTargetY = this.slot?.targetY ?? RACE_Y;
    const laneBias = (this.slot?.index ?? 0) % 2 === 0 ? -1 : 1;
    this.enterStartX  = Math.max(left, Math.min(right - SW, this.enterTargetX + laneBias * (24 + this.rand() * 24)));
    this.enterStartY  = -SH - 62 - this.rand() * 44;
    this.enterCtrlX   = Math.max(left, Math.min(right - SW, this.enterTargetX + laneBias * (70 + this.rand() * 34)));
    this.enterCtrlY   = this.enterTargetY - 128 - this.rand() * 48;
    this.entryProgress = 0;
    this.entryDuration = ENTRY_FRAMES_PROMOTED_TOP + Math.round(this.rand() * 18);
    this.entryMinProgress = 1 / this.entryDuration;
    this.entryDelayFrames = 0;
    this.x            = this.enterStartX;
    this.y            = this.enterStartY;
    this._resetMotionState();
    this.smoothedTargetX = this.enterTargetX;
  }

  placeForReconnect(slotX: number) {
    this.state        = 'RACING';
    this.x            = slotX - SW / 2;
    this.y            = this.slot?.targetY ?? RACE_Y;
    this._resetMotionState();
    this.enterTargetX = this.x;
    this.entryDelayFrames = 0;
    this.smoothedTargetX = this.x;
  }

  cashout(multiplier: number) {
    if (this.state !== 'RACING' && this.state !== 'ENTERING' && this.state !== 'STAGED' && this.state !== 'ROLLING_TO_START') return;
    this.state             = 'CASHING_OUT';
    this.cashoutMultiplier = multiplier;
    this.cashoutTimer      = 0;
    this.exitDrift         = 0;
    this.cashoutStartX     = this.x;
    this.cashoutStartY     = this.y;
    const { left, right } = getSlopeBounds(this.canvasWidth);
    this.cashoutCarveDir   = this.x + SW / 2 < (left + right) / 2 ? -1 : 1;
    this.cashoutTargetX    = Math.max(left, Math.min(right - SW, this.cashoutStartX + this.cashoutCarveDir * 72));
    this.moneyPopAlpha     = 1.0;
    this.steerTargetX = null;
    this.smoothedTargetX = this.x;
    this.commitFrames = 0;
  }

  beginCrash(presentation: CrashPresentation = 'explosion') {
    this.state = 'CRASHED';
    this.crashPresentation = presentation;
    this.crashFrames = 0;
    this.crashFallFrames = 0;
    this.crashDirection = this.presentationSeed % 2 === 0 ? 1 : -1;
    this.crashSpin = this.crashDirection * (0.055 + (this.presentationSeed % 7) * 0.006);
    this.snowPlow.clear();
    if (presentation === 'explosion') {
      this.vx = 0;
      this.explosion.trigger(this.x + SW / 2, this.y + SH / 2, this.tilt);
    } else {
      this.vx *= 0.35;
    }
  }

  // ── Update ─────────────────────────────────────────────────────────────────

  update(obstacles: Obstacle[], background: BackgroundScroller, elapsedSeconds = 0) {
    this.elapsedSec = elapsedSeconds;
    const bgSpeed   = background.getCurrentSpeed();

    if (this.skiTracks.length > 0) {
      for (const t of this.skiTracks) t.y -= bgSpeed;
      this.skiTracks = this.skiTracks.filter(t => t.y > -30);
    }
    this.snowPlow.update(bgSpeed);

    switch (this.state) {
      case 'ROLLING_TO_START': this._updateRollingToStart(bgSpeed); break;
      case 'STAGED':      this._updateStaged();             break;
      case 'ENTERING':    this._updateEntering(bgSpeed);   break;
      case 'RACING':      this._updateRacing(obstacles);   break;
      case 'CASHING_OUT': this._updateCashingOut(bgSpeed); break;
      case 'CRASHED':
        if (this.crashPresentation === 'avalanche') this._updateAvalancheCrash();
        else if (this.crashPresentation === 'snowball') this._updateSnowballCrash();
        else if (this.crashPresentation === 'snow_slab') this._updateSnowSlabCrash();
        else if (this.crashPresentation === 'cliff') this._updateCliffCrash();
        else this.explosion.update();
        break;
      default: break;
    }
  }

  private _updateEntering(bgSpeed: number) {
    if (this.entryDelayFrames > 0) {
      this.entryDelayFrames--;
      this._updateStaged();
      return;
    }
    const prevX = this.x;
    this.entryProgress = Math.min(1, this.entryProgress + Math.max(this.entryMinProgress, (bgSpeed / 3.2) / this.entryDuration));
    const t = this._easeOutCubic(this.entryProgress);
    const inv = 1 - t;
    this.x = inv * inv * this.enterStartX + 2 * inv * t * this.enterCtrlX + t * t * this.enterTargetX;
    this.y = inv * inv * this.enterStartY + 2 * inv * t * this.enterCtrlY + t * t * this.enterTargetY;
    this.vx = Math.max(-PATH_MAX_VX, Math.min(PATH_MAX_VX, this.x - prevX));
    this.targetTilt = Math.max(-1, Math.min(1, this.vx / PATH_MAX_VX));
    this.tilt += (this.targetTilt - this.tilt) * 0.10;
    this._emitSnowPlow(Math.max(Math.abs(this.vx) / PATH_MAX_VX, Math.abs(this.tilt)), 0.7);
    if (this.entryProgress >= 1) {
      this.x = this.enterTargetX; this.y = this.enterTargetY; this.vx *= 0.35;
      this.state = 'RACING';
    }
  }

  private _updateRollingToStart(bgSpeed: number) {
    this._updateEntering(bgSpeed);
    if (this.state === 'RACING') this._finishStartRoll();
  }

  private _updateStaged() {
    const idle = Math.sin(Date.now() / 420 + (this.slot?.swayPhase ?? 0)) * 1.2;
    this.x += ((this.enterTargetX + idle) - this.x) * 0.06;
  }

  private _finishStartRoll() {
    this.state = 'STAGED';
    this.enterTargetY = this._startLineY();
    this.x = this.enterTargetX;
    this.y = this.enterTargetY;
    this._resetMotionState();
    this.entryProgress = 0;
    this.entryDelayFrames = 0;
  }

  private _startLineY() {
    if (!this.slot) return START_LINE_CENTER_Y;
    return START_LINE_CENTER_Y + (this.slot.targetY - RACE_Y) * START_LINE_Y_SYNC;
  }

  private _resetMotionState(clearTracks = true) {
    this.vx = 0;
    this.tilt = 0;
    this.targetTilt = 0;
    this.steerTargetX = null;
    this.smoothedTargetX = null;
    this.commitFrames = 0;
    this.currentSpriteKey = 'down';
    if (clearTracks) {
      this.skiTracks = [];
      this.snowPlow.clear();
    }
  }

  private _updateAvalancheCrash() {
    this.crashFrames++;
    const push = Math.min(9.5, 1.2 + this.crashFrames * 0.105);
    this.y += push;
    this.x += this.vx + Math.sin(this.crashFrames * 0.18 + this.presentationSeed) * 0.7;
    this.vx *= 0.97;
    this.tilt += this.crashSpin;
  }

  private _updateSnowballCrash() {
    this.crashFrames++;
    const lateralKick = Math.min(10, 2.2 + this.crashFrames * 0.16);
    this.x += this.crashDirection * lateralKick;
    this.y += Math.min(7, 1.8 + this.crashFrames * 0.09);
    this.tilt += this.crashDirection * 0.15;
  }

  private _updateSnowSlabCrash() {
    this.crashFrames++;
    const slide = Math.min(9.5, 0.25 + this.crashFrames * 0.06);
    this.y += slide;
    this.x += this.crashDirection * Math.min(0.7, this.crashFrames * 0.012)
      + Math.sin(this.crashFrames * 0.11 + this.presentationSeed) * 0.18;
    this.tilt += this.crashSpin * 0.18;
  }

  private _updateCliffCrash() {
    this.crashFrames++;
    const lipY = Math.min(this.canvasHeight * 0.66, 270);
    if (this.getCenterY() < lipY + 4) {
      const approach = Math.min(5.8, 0.75 + this.crashFrames * 0.18);
      this.y += approach;
      this.x += this.vx * 0.22;
      this.tilt *= 0.96;
      this.crashFallFrames = 0;
      return;
    }
    this.crashFallFrames++;
    if (this.crashFallFrames <= 7) {
      // Brief Wile E. Coyote beat: still upright, just beyond the snow lip.
      this.y += 1.05;
      this.x += this.vx * 0.12;
      return;
    }
    const fallFrames = this.crashFallFrames - 7;
    this.y += Math.min(10, 1.5 + fallFrames * 0.16);
    this.x += this.crashDirection * Math.min(2.1, fallFrames * 0.035);
    this.tilt += this.crashSpin * 0.82;
  }

  private _updateRacing(obstacles: Obstacle[]) {
    // Single unified steering — handles path following and obstacle avoidance
    // in one pass so the skier always reasons about the full picture.
    this._steer(obstacles);

    // Apply lateral velocity
    this.x += this.vx;
    this._updateFormationY();

    // Smooth tilt interpolation — tilt lags velocity for a physical lean feel.
    // 0.07 (was 0.10) makes the lean build and decay more gradually, which
    // reduces how often tilt crosses sprite-change thresholds per second.
    this.tilt += (this.targetTilt - this.tilt) * 0.07;

    // Hard boundary clamp
    const { left, right } = getSlopeBounds(this.canvasWidth);
    if (this.x < left)       { this.x = left;       this.vx = Math.max(0, this.vx); }
    if (this.x > right - SW) { this.x = right - SW; this.vx = Math.min(0, this.vx); }

    // Record ski track positions
    this.skiTracks.unshift({ x: this.x + SW / 2, y: this.y + SH - 14 });
    if (this.skiTracks.length > MAX_TRACK) this.skiTracks.pop();
    this._emitSnowPlow(Math.max(Math.abs(this.vx) / PATH_MAX_VX, Math.abs(this.tilt)), 0.9);
  }

  private _updateCashingOut(bgSpeed: number) {
    this.cashoutTimer++;
    this.moneyPopAlpha = Math.max(0, 1 - Math.max(0, this.cashoutTimer - 12) / 55);

    const settleEnd = CASHOUT_HARD_SWERVE_FRAMES + CASHOUT_SOFT_SWERVE_FRAMES;

    if (this.cashoutTimer <= settleEnd) {
      const prevX = this.x;
      const hardT = Math.min(1, this.cashoutTimer / CASHOUT_HARD_SWERVE_FRAMES);
      const softT = Math.max(0, (this.cashoutTimer - CASHOUT_HARD_SWERVE_FRAMES) / CASHOUT_SOFT_SWERVE_FRAMES);
      const hardEase = this._easeOutCubic(hardT);
      const softEase = this._easeOutCubic(softT);
      const settleBack = this.cashoutCarveDir * 7 * softEase;
      const { left, right } = getSlopeBounds(this.canvasWidth);

      this.x = Math.max(left, Math.min(right - SW, this.cashoutStartX + (this.cashoutTargetX - this.cashoutStartX) * hardEase - settleBack));
      const exitRise = Math.max(0, this.cashoutTimer - 1) * Math.max(1.2, bgSpeed * 0.52);
      this.y = this.cashoutStartY - hardEase * 18 - softEase * 4 - exitRise;
      this.vx = this.x - prevX;
      this.targetTilt = this.cashoutTimer <= CASHOUT_HARD_SWERVE_FRAMES
        ? this.cashoutCarveDir * 0.95
        : this.cashoutCarveDir * 0.34 * (1 - softEase);
      this.tilt += (this.targetTilt - this.tilt) * 0.16;
      this.skiTracks.unshift({ x: this.x + SW / 2, y: this.y + SH - 14 });
      if (this.skiTracks.length > MAX_TRACK) this.skiTracks.pop();
      const hardCarveBoost = this.cashoutTimer <= CASHOUT_HARD_SWERVE_FRAMES ? 2.0 : 1.1;
      this._emitSnowPlow(Math.max(0.55, Math.abs(this.targetTilt)), hardCarveBoost);
    } else {
      this.targetTilt = 0;
      this.tilt += (this.targetTilt - this.tilt) * 0.10;
      this.exitDrift = Math.min(Math.max(2.8, bgSpeed * 1.3), this.exitDrift + 0.22);
      this.y        -= this.exitDrift;
    }

    if (this.y < -SH - 120) {
      this.state = 'EXITED';
      this.snowPlow.clear();
    }
  }

  private _emitSnowPlow(intensity: number, amountScale: number) {
    const clampedIntensity = Math.max(0, Math.min(1, intensity));
    if (clampedIntensity < 0.18) return;

    const carveDir = Math.abs(this.vx) > 0.2
      ? Math.sign(this.vx)
      : Math.sign(this.targetTilt || this.tilt || this.cashoutCarveDir || 1);
    const amount = amountScale * (clampedIntensity < 0.38 ? 1 : clampedIntensity < 0.68 ? 2 : 3);

    this.snowPlow.emit({
      x: this.x + SW / 2,
      y: this.y + SH - 10,
      carveDir,
      intensity: clampedIntensity,
      amount,
      rand: this.rand,
    });
  }

  private _targetMidY(): number {
    if (!this.slot) return RACE_Y + SH / 2;
    const sway = Math.sin(this.elapsedSec * this.slot.swayFreq * Math.PI * 2 + this.slot.swayPhase) * this.slot.swayAmp * 0.22;
    return this.slot.targetY + SH / 2 + sway;
  }

  private _updateFormationY() {
    const targetY = this._targetMidY() - SH / 2;
    this.y += (targetY - this.y) * FORMATION_Y_PULL;
  }

  private _easeOutCubic(t: number): number {
    return 1 - Math.pow(1 - Math.max(0, Math.min(1, t)), 3);
  }

  // ── Path following with obstacle override ─────────────────────────────────

  // ── Unified steering ──────────────────────────────────────────────────────

  /**
   * Single-pass steering that replaces the old evading/coasting/cruising
   * state machine entirely.
   *
   * The key architectural difference: the old system used _getThreats() which
   * only flagged obstacles overlapping the skier's *current* corridor.  So
   * after a dodge the skier saw a clear corridor, started returning, walked
   * into the next obstacle's corridor, triggered a new evasion — sawtooth.
   *
   * This method collects every obstacle in the full lookahead window with no
   * lateral filter, merges their padded X intervals into blocked zones, then
   * derives clear gaps across the whole slope.  The seeded path target is used
   * directly if it falls inside a clear gap; otherwise the skier routes to the
   * nearest viable gap.  Because ALL obstacles are considered at once, a cluster
   * of three trees produces one merged blocked zone and one gap target — the
   * skier commits to a single arc that clears all of them and only returns when
   * homeX drift has moved the reference point to a genuinely unblocked position.
   */
  private _steer(obstacles: Obstacle[]) {
    // The skier's own width follows its sprite scale; the berth around an obstacle follows
    // obstacle size, which scales with the canvas like the background (SlopeConfig.obstacleSize).
    const avoidHalf = SKIER_AVOID_HALF * this.spriteScale, obsPad = OBS_PAD * (this.canvasWidth / 960), minGap = MIN_GAP * this.spriteScale;
    const { left, right } = getSlopeBounds(this.canvasWidth);

    // ── 1. All obstacles in lookahead — full slope width, no lateral filter ─
    const ahead = obstacles.filter(o =>
      o.y < this.y + FAR_AHEAD &&
      o.y + o.height > this.y - BEHIND_SCAN
    );

    // ── 2. Blocked intervals → clear gaps ────────────────────────────────
    const relevantAhead = ahead.filter(o => {
      const vertical = Math.max(0, o.y - this.y);
      const obsMid = o.x + o.width / 2;
      const myMid = this.x + SW / 2;
      const corridor = avoidHalf + obsPad + Math.min(170, 48 + vertical * 0.30 + Math.abs(this.vx) * 22);
      return vertical < NEAR_AHEAD || Math.abs(obsMid - myMid) < corridor + o.width / 2;
    });
    let blocked    = this._mergeIntervals(
      relevantAhead.map(o => ({ s: o.x - obsPad, e: o.x + o.width + obsPad }))
    );
    let gaps       = this._computeGaps(blocked, left, right);
    let viableGaps = gaps.filter(g => g.w >= minGap);

    // ── 3. Preferred target from seeded path blended with evolved homeX ──
    let preferredX: number;
    if (this.lanePath) {
      const lookAheadT   = this.elapsedSec + PATH_LOOKAHEAD_SEC;
      const pathNormX    = this.lanePath.getNormX(lookAheadT);
      const slotNormX = this.slot
        ? Math.max(0.04, Math.min(0.96, this.slot.anchorNormX + Math.sin(lookAheadT * this.slot.swayFreq * Math.PI * 2 + this.slot.swayPhase) * (this.slot.swayAmp / Math.max(1, right - left))))
        : this.homeX;
      const blendedNormX = pathNormX * 0.42 + slotNormX * 0.42 + this.homeX * 0.16;
      preferredX = this.lanePath.toCanvasX(blendedNormX, this.canvasWidth, SW);
    } else {
      preferredX = left + this.homeX * (right - left - SW);
    }

    const myMidAfterPreference = this.x + SW / 2;
    const preferredMidAfterPreference = preferredX + SW / 2;
    const crossingMin = Math.min(myMidAfterPreference, preferredMidAfterPreference) - avoidHalf - obsPad;
    const crossingMax = Math.max(myMidAfterPreference, preferredMidAfterPreference) + avoidHalf + obsPad;
    const crossingAwareAhead = ahead.filter(o => {
      const vertical = Math.max(0, o.y - this.y);
      const obsMid = o.x + o.width / 2;
      const corridor = avoidHalf + obsPad + Math.min(180, 54 + vertical * 0.35 + Math.abs(this.vx) * 26);
      const crossesDesiredMove = o.x + o.width + obsPad >= crossingMin && o.x - obsPad <= crossingMax;
      return vertical < NEAR_AHEAD || crossesDesiredMove || Math.abs(obsMid - myMidAfterPreference) < corridor + o.width / 2;
    });
    blocked = this._mergeIntervals(
      crossingAwareAhead.map(o => ({ s: o.x - obsPad, e: o.x + o.width + obsPad }))
    );
    gaps = this._computeGaps(blocked, left, right);
    viableGaps = gaps.filter(g => g.w >= minGap);

    // ── 4 & 5. Is the preferred target inside a clear gap? ───────────────
    const preferredMid = preferredX + SW / 2;
    const pathIsClear  = viableGaps.some(g =>
      preferredMid >= g.cx - g.w / 2 + avoidHalf &&
      preferredMid <= g.cx + g.w / 2 - avoidHalf
    );

    let finalTargetX: number;
    let isAvoiding = false;

    if (viableGaps.length === 0 || pathIsClear) {
      // Path ahead is clear — follow the preferred target naturally.
      finalTargetX = preferredX;
    } else {
      // Preferred target is blocked. Route to the nearest viable gap.
      // Because all obstacles are merged together, this single gap target
      // clears the entire cluster — no repeated re-evaluation per obstacle.
      isAvoiding   = true;
      const myMidX = this.x + SW / 2;
      const slopeMid = (left + right) / 2;
      const scoreGap = (g: { cx: number; w: number }) => {
        const travelPenalty = Math.abs(g.cx - myMidX);
        const edgePenalty = Math.abs(g.cx - slopeMid) * 0.22;
        const widthBonus = Math.min(90, g.w - minGap) * 0.85;
        return travelPenalty + edgePenalty - widthBonus;
      };
      const best   = viableGaps.reduce((a, b) =>
        scoreGap(b) < scoreGap(a) ? b : a
      );
      finalTargetX = best.cx - SW / 2;
    }

    // ── 6. Commit window ──────────────────────────────────────────────────
    // Avoiding: commit for COMMIT_FRAMES before re-evaluating so the skier
    // doesn't oscillate between near-equal gap choices mid-dodge.
    // Clear path: short 8-frame window just smooths single-frame flickers.
    if (this.commitFrames <= 0 || this.steerTargetX === null) {
      this.steerTargetX = finalTargetX;
      this.commitFrames = isAvoiding ? COMMIT_FRAMES : 8;
    } else {
      this.commitFrames--;
      // While committed to an avoidance target, allow an override only if a
      // meaningfully closer gap has appeared — prevents missing an easy escape
      // route while sailing toward a more distant one.
      if (isAvoiding && this.steerTargetX !== null) {
        const myMidX      = this.x + SW / 2;
        const targetMidX  = this.steerTargetX + SW / 2;
        const targetBlocked = !viableGaps.some(g =>
          targetMidX >= g.cx - g.w / 2 + avoidHalf &&
          targetMidX <= g.cx + g.w / 2 - avoidHalf
        );
        const currentDist = Math.abs(this.steerTargetX + SW / 2 - myMidX);
        const newDist     = Math.abs(finalTargetX      + SW / 2 - myMidX);
        if (targetBlocked || newDist < currentDist - 20) {
          this.steerTargetX = finalTargetX;
          this.commitFrames = COMMIT_FRAMES;
        }
      }
    }

    // ── 7. homeX drift ────────────────────────────────────────────────────
    // Gradually relocate the reference point toward wherever the skier
    // actually is.  After a dodge this means preferredX will start from the
    // post-dodge position, so when obstacles finally clear the seeded path
    // pulls from the new position — no sudden snap back to the old lane.
    const currentNormX = Math.max(0.04, Math.min(0.96,
      (this.x + SW / 2 - left) / (right - left)
    ));
    const anchorNormX = this.slot ? this.slot.anchorNormX : currentNormX;
    const driftTarget = currentNormX * 0.58 + anchorNormX * 0.42;
    this.homeX += (driftTarget - this.homeX) * HOME_DRIFT_RATE;

    // ── 8. Apply proportional steer ───────────────────────────────────────
    // Near threats (within NEAR_AHEAD) get a stronger gain so the skier
    // responds urgently.  Far threats use the standard gain for smooth arcs.
    const hasNearThreat = isAvoiding &&
      crossingAwareAhead.some(o => o.y < this.y + NEAR_AHEAD);
    const gain    = hasNearThreat ? PATH_STEER * 1.45 : PATH_STEER * 0.78;
    const damping = hasNearThreat ? 0.91 : 0.955;

    const rawTargetX = this.steerTargetX ?? finalTargetX;
    if (this.smoothedTargetX === null) this.smoothedTargetX = rawTargetX;
    const targetSmooth = hasNearThreat ? STEER_TARGET_SMOOTH * 2.4 : STEER_TARGET_SMOOTH;
    this.smoothedTargetX += (rawTargetX - this.smoothedTargetX) * targetSmooth;
    const dx = this.smoothedTargetX - this.x;
    const desiredVx = Math.max(-PATH_MAX_VX, Math.min(PATH_MAX_VX, dx * gain * 11));
    const accel = Math.max(-STEER_ACCEL_LIMIT, Math.min(STEER_ACCEL_LIMIT, desiredVx - this.vx));
    this.vx  = this.vx * damping + accel;
    this.vx  = Math.max(-PATH_MAX_VX, Math.min(PATH_MAX_VX, this.vx));
    this.targetTilt = Math.max(-1, Math.min(1, this.vx / PATH_MAX_VX));
  }

  // ── Interval / gap helpers ────────────────────────────────────────────────

  /** Merge an array of {s,e} intervals into non-overlapping sorted blocks. */
  private _mergeIntervals(intervals: { s: number; e: number }[]): { s: number; e: number }[] {
    if (intervals.length === 0) return [];
    const sorted = [...intervals].sort((a, b) => a.s - b.s);
    const merged: { s: number; e: number }[] = [{ ...sorted[0] }];
    for (let i = 1; i < sorted.length; i++) {
      const last = merged[merged.length - 1];
      if (sorted[i].s <= last.e) {
        last.e = Math.max(last.e, sorted[i].e);
      } else {
        merged.push({ ...sorted[i] });
      }
    }
    return merged;
  }

  /**
   * Derive clear gap descriptors from merged blocked intervals within
   * [left, right].  Each gap is { cx: centre X, w: width }.
   */
  private _computeGaps(
    blocked: { s: number; e: number }[],
    left: number,
    right: number
  ): { cx: number; w: number }[] {
    const gaps: { cx: number; w: number }[] = [];
    let cursor = left;
    for (const block of blocked) {
      if (block.s > cursor) {
        gaps.push({ cx: cursor + (block.s - cursor) / 2, w: block.s - cursor });
      }
      cursor = Math.max(cursor, block.e);
    }
    if (cursor < right) {
      gaps.push({ cx: cursor + (right - cursor) / 2, w: right - cursor });
    }
    return gaps;
  }

  // ── Draw ──────────────────────────────────────────────────────────────────

  drawTracks(ctx: CanvasRenderingContext2D, currentMultiplier = 1) {
    if (this.state === 'QUEUED' || this.state === 'ROLLING_TO_START' || this.state === 'STAGED' || this.state === 'EXITED' || this.state === 'CRASHED') return;
    if (this.skiTracks.length >= 2) {
      const aurora = currentMultiplier >= 25;
      const radiance = aurora ? Math.min(1, (currentMultiplier - 25) / 75) : 0;
      ctx.save(); ctx.lineCap = 'round';
      if (aurora) { ctx.globalCompositeOperation = 'screen'; ctx.shadowColor = 'rgba(107,244,226,.55)'; ctx.shadowBlur = 5 + radiance * 4; }
      for (let i = 1; i < this.skiTracks.length; i++) {
        const t = this.skiTracks[i], p = this.skiTracks[i - 1];
        const a = (1 - i / MAX_TRACK) * (aurora ? .34 + radiance * .16 : .26);
        ctx.strokeStyle = aurora ? `rgba(120,247,229,${a})` : `rgba(180,200,220,${a})`; ctx.lineWidth = aurora ? 2.1 : 1.8;
        ctx.beginPath(); ctx.moveTo(p.x - 8, p.y); ctx.lineTo(t.x - 8, t.y); ctx.stroke();
        if (aurora) ctx.strokeStyle = `rgba(255,221,150,${a * .78})`;
        ctx.beginPath(); ctx.moveTo(p.x + 8, p.y); ctx.lineTo(t.x + 8, t.y); ctx.stroke();
      }
      ctx.restore();
    }
    this.snowPlow.draw(ctx);
  }

  drawBody(ctx: CanvasRenderingContext2D, currentMultiplier: number) {
    if (this.state === 'QUEUED' || this.state === 'EXITED') return;
    if (this.state === 'CRASHED' && this.crashPresentation === 'explosion') {
      const ex = this.x + SW / 2, ey = this.y + SH / 2, k = this.spriteScale;
      ctx.save(); ctx.translate(ex, ey); ctx.scale(k, k); ctx.translate(-ex, -ey);
      this.explosion.draw(ctx); ctx.restore(); return;
    }
    const pal = CHROMA_PALETTES[this.chroma];
    ctx.save();
    const cx = this.x + SW / 2, cy = this.y + SH / 2;
    ctx.translate(cx, cy);
    ctx.scale(this.spriteScale, this.spriteScale);
    if (this.state === 'CRASHED' && this.crashPresentation === 'cliff') {
      const lipY = Math.min(this.canvasHeight * 0.66, 270);
      const fallDepth = Math.max(0, this.getCenterY() - lipY);
      const activeFallDepth = this.crashFallFrames <= 7 ? 0 : fallDepth;
      const fallScale = Math.max(0.22, 1 - activeFallDepth / 215);
      ctx.globalAlpha = Math.max(0.12, 1 - activeFallDepth / 185);
      ctx.scale(fallScale, fallScale);
    }
    ctx.rotate(this.state === 'CRASHED' ? this.tilt : this.tilt * 0.3);
    ctx.shadowColor = pal.glow; ctx.shadowBlur = 12 + Math.sin(Date.now() / 380) * 3;
    ctx.fillStyle = 'rgba(0,0,0,0.18)'; ctx.beginPath();
    ctx.ellipse(0, SH / 2 - 8, 17, 7, 0, 0, Math.PI * 2); ctx.fill();
    const spriteKey = this._presentationSpriteKey();
    const sprite = this._choosePresentationSprite(spriteKey);
    const loaded = sprite.complete && sprite.naturalWidth > 0;
    drawSkierSprite(ctx, loaded ? sprite : cachedImg(SkiierDown), loaded ? spriteKey : 'down', 22, pal.label);
    if (this.state === 'CASHING_OUT' && this.cashoutTimer < 55) {
      ctx.shadowBlur = 0; ctx.globalAlpha = Math.min(1, this.cashoutTimer / 12);
      ctx.font = '17px serif'; ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
      ctx.fillText('🏁', SW / 2 - 2, -SH / 4);
    }
    ctx.restore();
    if (this.state !== 'CRASHED') this._drawLabel(ctx, pal);
    if (this.state === 'CASHING_OUT' && this.moneyPopAlpha > 0) this._drawMoneyPop(ctx, pal.label);
  }

  /** Name tag: the rider's fun nickname, or "YOU" on this player's own skier. No money on the slope. */
  private _drawLabel(ctx: CanvasRenderingContext2D, pal: typeof CHROMA_PALETTES[number]) {
    const cx = this.x + SW / 2, ty = this.y + (SH / 2) * (1 - this.spriteScale) - 5;
    const text = this.isSelf ? 'YOU' : maskPlayerName(this.username, this.playerId);
    ctx.save();
    ctx.font = this.isSelf ? '800 10px JetBrains Mono,monospace' : 'bold 8.5px JetBrains Mono,monospace';
    ctx.textAlign = 'center';
    const tw = ctx.measureText(text).width, h = this.isSelf ? 15 : 14;
    ctx.fillStyle = pal.tagBg; ctx.strokeStyle = pal.glow.replace('0.80)', this.isSelf ? '0.85)' : '0.45)'); ctx.lineWidth = this.isSelf ? 1.2 : 0.7;
    ctx.beginPath(); (ctx as any).roundRect(cx - tw / 2 - 6, ty - h + 1, tw + 12, h, 4); ctx.fill(); ctx.stroke();
    ctx.fillStyle = pal.label; ctx.fillText(text, cx, ty - 2);
    ctx.restore();
  }

  private _drawMoneyPop(ctx: CanvasRenderingContext2D, color: string) {
    if (!this.cashoutMultiplier) return;
    const cx = this.x + SW / 2, rise = Math.max(0, this.cashoutTimer - 10) * 0.55;
    const popY = this.y + (SH / 2) * (1 - this.spriteScale) - 28 - rise;
    const profit = (this.cashoutMultiplier - 1) * this.betAmount;
    const txt = profit >= 0 ? `+${formatMoney(profit)}` : `-${formatMoney(Math.abs(profit))}`;
    ctx.save(); ctx.globalAlpha = this.moneyPopAlpha;
    ctx.font = 'bold 14px Barlow Condensed,Arial'; ctx.textAlign = 'center';
    ctx.strokeStyle = 'rgba(0,0,0,0.9)'; ctx.lineWidth = 3.5; ctx.fillStyle = color;
    ctx.strokeText(txt, cx, popY); ctx.fillText(txt, cx, popY); ctx.restore();
  }

  private _presentationSpriteKey():
    'hardLeft' | 'softLeft' | 'down' | 'softRight' | 'hardRight' | 'swerveSoft' | 'swerveAbrupt' | 'idleRest' {
    if (this.state === 'CASHING_OUT') {
      if (this.cashoutTimer <= CASHOUT_HARD_SWERVE_FRAMES) return 'swerveAbrupt';
      if (this.cashoutTimer <= CASHOUT_HARD_SWERVE_FRAMES + CASHOUT_SOFT_SWERVE_FRAMES) return 'swerveSoft';
      return 'idleRest';
    }

    if (this.state === 'STAGED') return 'idleRest';

    if (this.state === 'ROLLING_TO_START') {
      if (this.entryProgress < 0.55) return 'swerveSoft';
      if (this.entryProgress < 0.92) return 'swerveAbrupt';
      return 'idleRest';
    }

    return this._tiltSpriteKey();
  }

  private _choosePresentationSprite(
    key: 'hardLeft' | 'softLeft' | 'down' | 'softRight' | 'hardRight' | 'swerveSoft' | 'swerveAbrupt' | 'idleRest' = this._presentationSpriteKey()
  ): HTMLImageElement {
    switch (key) {
      case 'swerveSoft':   return cachedImg(SkiierAbruptSwerveSoft);
      case 'swerveAbrupt': return cachedImg(SkiierAbruptSwerve);
      case 'idleRest':     return cachedImg(SkiierIdleRestV3);
      case 'hardLeft':     return cachedImg(SkiierHardLeft);
      case 'softLeft':     return cachedImg(SkiierSoftLeft);
      case 'softRight':    return cachedImg(SkiierSoftRight);
      case 'hardRight':    return cachedImg(SkiierHardRight);
      default:             return cachedImg(SkiierDown);
    }
  }

  /**
   * Choose the correct sprite with Schmitt-trigger hysteresis.
   *
   * Each sprite has an ENTRY threshold (how far tilt must go to switch INTO
   * it) and an EXIT threshold (how far tilt must recover before switching
   * AWAY from it).  The exit threshold is always closer to zero than the
   * entry threshold, creating a deadband that prevents chattering when tilt
   * hovers near a boundary.
   *
   * Example for softLeft:
   *   Entry:  tilt drops below -0.18  → switch to softLeft
   *   Exit:   tilt recovers above -0.08 → allowed to switch back to down
   *
   * Without this, a tilt oscillating between -0.12 and -0.16 would swap
   * sprites every few frames.  With it, once softLeft is active the skier
   * must recover past -0.08 before down can re-engage — a 0.10 deadband.
   */
  private _tiltSpriteKey(): 'hardLeft' | 'softLeft' | 'down' | 'softRight' | 'hardRight' {
    const t   = this.tilt;
    const cur = this.currentSpriteKey;

    // ── Entry thresholds (must cross to enter a sprite) ───────────────────
    const SOFT_ENTRY  = 0.18;   // |tilt| must exceed this to enter soft lean
    const HARD_ENTRY  = 0.52;   // |tilt| must exceed this to enter hard lean

    // ── Exit thresholds (tilt must recover past this to leave a sprite) ───
    // Deadband = ENTRY - EXIT.  Wider deadband = less chattering.
    const SOFT_EXIT   = 0.08;   // deadband of 0.10 around soft threshold
    const HARD_EXIT   = 0.38;   // deadband of 0.14 around hard threshold

    let next = cur;

    switch (cur) {
      case 'down':
        if      (t <= -SOFT_ENTRY) next = 'softLeft';
        else if (t >=  SOFT_ENTRY) next = 'softRight';
        break;

      case 'softLeft':
        if      (t <= -HARD_ENTRY) next = 'hardLeft';
        else if (t >= -SOFT_EXIT)  next = 'down';
        break;

      case 'softRight':
        if      (t >=  HARD_ENTRY) next = 'hardRight';
        else if (t <=  SOFT_EXIT)  next = 'down';
        break;

      case 'hardLeft':
        if (t >= -HARD_EXIT) next = 'softLeft';
        break;

      case 'hardRight':
        if (t <=  HARD_EXIT) next = 'softRight';
        break;
    }

    this.currentSpriteKey = next;
    return next;
  }

  private _chooseSprite(): HTMLImageElement {
    return this._choosePresentationSprite();
  }

  getX()            { return this.x; }
  getY()            { return this.y; }
  getCenterX()      { return this.x + SW / 2; }
  getCenterY()      { return this.y + SH / 2; }
  getSpacingRadius(){ return this.slot?.spacingRadius ?? 62; }
  getPresentationSlot() { return this.slot; }
  getDebugPose() { return { x: this.x, y: this.y, centerX: this.getCenterX(), centerY: this.getCenterY(), state: this.state, slot: this.slot, tilt: this.tilt, vx: this.vx, crashPresentation: this.crashPresentation, crashFrames: this.crashFrames, avalancheFrames: this.crashFrames, spriteKey: this._presentationSpriteKey(), snowPlowParticles: this.snowPlow.getCount(), maxSnowPlowParticles: this.snowPlow.getMaxParticles() }; }
  isExplosionDone() {
    if (this.state !== 'CRASHED') return false;
    if (this.crashPresentation === 'explosion') return !this.explosion.isPlaying();
    return this.crashFrames > 150
      || this.y > this.canvasHeight + SH
      || this.x < -SW * 2
      || this.x > this.canvasWidth + SW;
  }

  applyNudge(dx: number) {
    if (this.state !== 'RACING') return;
    const MAX_NUDGE = 1.8;
    this.vx += Math.max(-MAX_NUDGE, Math.min(MAX_NUDGE, dx));
  }

  applyFormationForce(dx: number, dy: number) {
    if (this.state !== 'RACING' && this.state !== 'ENTERING') return;
    this.vx += Math.max(-0.48, Math.min(0.48, dx));
    this.y += Math.max(-0.34, Math.min(0.34, dy));
  }

  blendTowardSlotAnchor() {
    if (!this.slot || this.state !== 'RACING') return;
    const { left, right } = getSlopeBounds(this.canvasWidth);
    const targetX = left + this.slot.anchorNormX * (right - left) - SW / 2;
    this.vx += Math.max(-0.24, Math.min(0.24, (targetX - this.x) * 0.006 * FORMATION_X_BLEND));
  }
}
